// src/services/goalService.js
import { db, auth } from '../config/firebase';
import { 
  collection, doc, getDoc, getDocs, setDoc, addDoc, updateDoc, deleteDoc, 
  query, where, orderBy, serverTimestamp, writeBatch, arrayUnion, arrayRemove
} from 'firebase/firestore';
import { 
  DEFAULT_GOAL, DEFAULT_PERIOD, DEFAULT_LEAD_MEASURE, DEFAULT_WEEKLY_SCORE,
  calculateWeeklyScore, calculateGoalProgress, getCurrentWeekNumber,
  generateWeekDates, getPeriodEndDate
} from '../constants/goalTypes';

/**
 * GoalService - 12-Week Year Goal Management
 * 
 * Collections:
 * - pb-books/{bookId}/goal-periods     → 12-week periods
 * - pb-books/{bookId}/goals            → Individual goals
 * - pb-books/{bookId}/weekly-scores    → Weekly scorecards
 */

export const goalService = {

  // ============================================================
  // 12-WEEK PERIODS
  // ============================================================
  
  async getAllPeriods(bookId, options = {}) {
    try {
      const { workspaceId, includeCompleted = false } = options;
      const periodsRef = collection(db, 'pb-books', bookId, 'goal-periods');
      
      const snapshot = await getDocs(query(periodsRef, orderBy('startDate', 'desc')));
      let periods = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Filter by workspace
      if (workspaceId !== undefined) {
        periods = periods.filter(p => p.workspaceId === workspaceId);
      }
      
      // Filter out completed unless requested
      if (!includeCompleted) {
        periods = periods.filter(p => p.status !== 'completed');
      }
      
      // Add computed fields
      const today = new Date();
      periods = periods.map(p => ({
        ...p,
        currentWeek: getCurrentWeekNumber(p.startDate),
        hasStarted: new Date(p.startDate) <= today,
        hasEnded: new Date(p.endDate) < today,
        isActive: p.status === 'active' && new Date(p.startDate) <= today && new Date(p.endDate) >= today,
        isFuture: new Date(p.startDate) > today
      }));
      
      return periods;
    } catch (error) {
      console.error('Error getting all periods:', error);
      return [];
    }
  },

  async getPeriodsByWorkspace(bookId, workspaces = []) {
    try {
      const allPeriods = await this.getAllPeriods(bookId, { includeCompleted: false });
      
      // Group by workspace
      const grouped = {
        personal: allPeriods.filter(p => !p.workspaceId),
        ...workspaces.reduce((acc, ws) => {
          acc[ws.id] = allPeriods.filter(p => p.workspaceId === ws.id);
          return acc;
        }, {})
      };
      
      return grouped;
    } catch (error) {
      console.error('Error getting periods by workspace:', error);
      return { personal: [] };
    }
  },

  // Get active period for specific workspace (or personal if null)
  async getWorkspaceActivePeriod(bookId, workspaceId = null) {
    try {
      const periodsRef = collection(db, 'pb-books', bookId, 'goal-periods');
      let q;
      
      if (workspaceId) {
        q = query(periodsRef, 
          where('workspaceId', '==', workspaceId),
          where('status', '==', 'active')
        );
      } else {
        // Personal periods (no workspaceId or null)
        q = query(periodsRef, where('status', '==', 'active'));
      }
      
      const snapshot = await getDocs(q);
      const periods = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(p => workspaceId ? p.workspaceId === workspaceId : !p.workspaceId);
      
      return periods[0] || null;
    } catch (error) {
      console.error('Error getting workspace active period:', error);
      return null;
    }
  },

  // Activate a draft/future period
  async activatePeriod(bookId, periodId) {
    if (!periodId) throw new Error('Period ID required');
    
    try {
      const docRef = doc(db, 'pb-books', bookId, 'goal-periods', periodId);
      await updateDoc(docRef, {
        status: 'active',
        activatedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // Also activate all draft goals in this period
      const goalsRef = collection(db, 'pb-books', bookId, 'goals');
      const goalsQuery = query(goalsRef, where('periodId', '==', periodId), where('status', '==', 'draft'));
      const goalsSnap = await getDocs(goalsQuery);
      
      const batch = writeBatch(db);
      goalsSnap.docs.forEach(goalDoc => {
        batch.update(goalDoc.ref, { 
          status: 'active',
          activatedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      });
      await batch.commit();
      
      return true;
    } catch (error) {
      console.error('Error activating period:', error);
      throw error;
    }
  },

  // Create period with draft status for future
  async createFuturePeriod(bookId, periodData) {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('User must be authenticated');

      const periodsRef = collection(db, 'pb-books', bookId, 'goal-periods');
      const docRef = doc(periodsRef);
      
      const startDate = new Date(periodData.startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // If start date is in future, create as draft
      const status = startDate > today ? 'draft' : 'active';
      
      const period = {
        ...DEFAULT_PERIOD,
        ...periodData,
        id: docRef.id,
        endDate: periodData.endDate || getPeriodEndDate(periodData.startDate),
        weeks: generateWeekDates(periodData.startDate),
        status,
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      await setDoc(docRef, period);
      return { id: docRef.id, ...period };
    } catch (error) {
      console.error('Error creating future period:', error);
      throw error;
    }
  },

  // Get multi-workspace dashboard
  async getMultiWorkspaceDashboard(bookId, workspaces = []) {
    try {
      const allPeriods = await this.getAllPeriods(bookId, { includeCompleted: false });
      const allGoals = await this.getGoals(bookId, {});
      
      // Build dashboard for each workspace context
      const dashboard = {
        personal: {
          periods: allPeriods.filter(p => !p.workspaceId),
          activePeriod: allPeriods.find(p => !p.workspaceId && p.status === 'active'),
          goals: allGoals.filter(g => !g.workspaceId),
          activeGoals: allGoals.filter(g => !g.workspaceId && g.status === 'active').length
        }
      };
      
      for (const ws of workspaces) {
        const wsPeriods = allPeriods.filter(p => p.workspaceId === ws.id);
        const wsGoals = allGoals.filter(g => g.workspaceId === ws.id);
        
        dashboard[ws.id] = {
          workspace: ws,
          periods: wsPeriods,
          activePeriod: wsPeriods.find(p => p.status === 'active'),
          goals: wsGoals,
          activeGoals: wsGoals.filter(g => g.status === 'active').length
        };
      }
      
      return dashboard;
    } catch (error) {
      console.error('Error getting multi-workspace dashboard:', error);
      return { personal: { periods: [], goals: [], activeGoals: 0 } };
    }
  },

  async getPeriods(bookId, options = {}) {
    try {
      const { status, workspaceId } = options;
      const periodsRef = collection(db, 'pb-books', bookId, 'goal-periods');
      let q = query(periodsRef, orderBy('startDate', 'desc'));
      
      const snapshot = await getDocs(q);
      let periods = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      if (status) {
        periods = periods.filter(p => p.status === status);
      }
      if (workspaceId) {
        periods = periods.filter(p => p.workspaceId === workspaceId || !p.workspaceId);
      }
      
      return periods;
    } catch (error) {
      console.error('Error getting periods:', error);
      return [];
    }
  },

  async getActivePeriod(bookId, workspaceId = null) {
    try {
        console.log('Getting active period for book:', bookId);
        
        const periodsRef = collection(db, 'pb-books', bookId, 'goal-periods');
        const q = query(periodsRef, where('status', '==', 'active'));
        
        const snapshot = await getDocs(q);
        console.log('Found periods:', snapshot.docs.length);
        
        const periods = snapshot.docs.map(doc => {
        console.log('Period doc:', doc.id, doc.data());
        return { id: doc.id, ...doc.data() };
        });
        
        // Filter by workspace if provided
        let filtered = periods;
        if (workspaceId) {
        filtered = periods.filter(p => p.workspaceId === workspaceId || !p.workspaceId);
        }
        
        return filtered[0] || null;
    } catch (error) {
        console.error('Error getting active period:', error);
        return null;
    }
    },

  async getPeriod(bookId, periodId) {
    if (!periodId) return null;  // ← ADD THIS GUARD
    
    try {
        const docRef = doc(db, 'pb-books', bookId, 'goal-periods', periodId);
        const docSnap = await getDoc(docRef);
        return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
    } catch (error) {
        console.error('Error getting period:', error);
        return null;
    }
    },

  async createPeriod(bookId, periodData) {
    try {
        const currentUser = auth.currentUser;
        if (!currentUser) throw new Error('User must be authenticated');

        // Generate ID first
        const periodsRef = collection(db, 'pb-books', bookId, 'goal-periods');
        const docRef = doc(periodsRef); // Creates ref with auto-generated ID
        
        const period = {
        ...DEFAULT_PERIOD,
        ...periodData,
        id: docRef.id,  // ← Use the generated ID
        endDate: periodData.endDate || getPeriodEndDate(periodData.startDate),
        weeks: generateWeekDates(periodData.startDate),
        status: 'active',
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
        };
        
        await setDoc(docRef, period);  // ← Use setDoc instead of addDoc
        return { id: docRef.id, ...period };
    } catch (error) {
        console.error('Error creating period:', error);
        throw error;
    }
    },

  async updatePeriod(bookId, periodId, updates) {
    if (!periodId) throw new Error('Period ID required');  // ← ADD
    
    try {
        const docRef = doc(db, 'pb-books', bookId, 'goal-periods', periodId);
        await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
        });
        return true;
    } catch (error) {
        console.error('Error updating period:', error);
        throw error;
    }
    },

    async completePeriod(bookId, periodId) {
        if (!periodId) throw new Error('Period ID required');  // ← ADD
        
        try {
            const docRef = doc(db, 'pb-books', bookId, 'goal-periods', periodId);
            await updateDoc(docRef, {
            status: 'completed',
            completedAt: serverTimestamp(),
            updatedAt: serverTimestamp()
            });
            return true;
        } catch (error) {
            console.error('Error completing period:', error);
            throw error;
        }
    },

  // ============================================================
  // GOALS
  // ============================================================

  async getGoals(bookId, options = {}) {
    try {
        console.log('Getting goals for book:', bookId, 'options:', options);
        
        const { periodId, status, category, workspaceId } = options;
        const goalsRef = collection(db, 'pb-books', bookId, 'goals');
        
        const snapshot = await getDocs(goalsRef);
        
        let goals = snapshot.docs.map(doc => {
        return { id: doc.id, ...doc.data() };
        });
        
        if (periodId) {
        goals = goals.filter(g => g.periodId === periodId);
        }
        if (status) goals = goals.filter(g => g.status === status);
        if (category) goals = goals.filter(g => g.category === category);
        if (workspaceId) goals = goals.filter(g => g.workspaceId === workspaceId || !g.workspaceId);
        
        // Calculate progress for each goal
        goals = goals.map(goal => ({
        ...goal,
        calculatedProgress: calculateGoalProgress(goal)
        }));
        
        return goals;
    } catch (error) {
        console.error('Error getting goals:', error);
        return [];
    }
    },

  async getGoal(bookId, goalId) {
    if (!goalId) return null;  // ← ADD
    
    try {
        const docRef = doc(db, 'pb-books', bookId, 'goals', goalId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
        const goal = { id: docSnap.id, ...docSnap.data() };
        goal.calculatedProgress = calculateGoalProgress(goal);
        return goal;
        }
        return null;
    } catch (error) {
        console.error('Error getting goal:', error);
        return null;
    }
    },

  async createGoal(bookId, goalData, periodId = null) {
    try {
        const currentUser = auth.currentUser;
        if (!currentUser) throw new Error('User must be authenticated');

        // Generate ID first
        const goalsRef = collection(db, 'pb-books', bookId, 'goals');
        const docRef = doc(goalsRef); // Creates ref with auto-generated ID
        
        // Get period if not provided
        let period = null;
        if (periodId) {
        period = await this.getPeriod(bookId, periodId);
        } else {
        period = await this.getActivePeriod(bookId, goalData.workspaceId);
        }
        
        const goal = {
        ...DEFAULT_GOAL,
        ...goalData,
        id: docRef.id,  // ← Use the generated ID
        periodId: period?.id || null,
        periodStartDate: period?.startDate || null,
        periodEndDate: period?.endDate || null,
        status: 'active',
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
        };
        
        await setDoc(docRef, goal);  // ← Use setDoc instead of addDoc
        
        // Add goal to period
        if (period?.id) {
        await this.updatePeriod(bookId, period.id, {
            goalIds: arrayUnion(docRef.id)
        });
        }
        
        return { id: docRef.id, ...goal };
    } catch (error) {
        console.error('Error creating goal:', error);
        throw error;
    }
  },

  async updateGoal(bookId, goalId, updates) {
    try {
      const docRef = doc(db, 'pb-books', bookId, 'goals', goalId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error updating goal:', error);
      throw error;
    }
  },

  async deleteGoal(bookId, goalId) {
    try {
      // Get goal to find period
      const goal = await this.getGoal(bookId, goalId);
      
      // Remove from period
      if (goal?.periodId) {
        await this.updatePeriod(bookId, goal.periodId, {
          goalIds: arrayRemove(goalId)
        });
      }
      
      // Delete weekly scores for this goal
      const scoresRef = collection(db, 'pb-books', bookId, 'weekly-scores');
      const scoresQuery = query(scoresRef, where('goalId', '==', goalId));
      const scoresSnap = await getDocs(scoresQuery);
      
      const batch = writeBatch(db);
      scoresSnap.docs.forEach(doc => batch.delete(doc.ref));
      
      // Delete goal
      batch.delete(doc(db, 'pb-books', bookId, 'goals', goalId));
      
      await batch.commit();
      return true;
    } catch (error) {
      console.error('Error deleting goal:', error);
      throw error;
    }
  },

  async completeGoal(bookId, goalId, completionNotes = '') {
    try {
      const docRef = doc(db, 'pb-books', bookId, 'goals', goalId);
      await updateDoc(docRef, {
        status: 'completed',
        completionNotes,
        completedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Error completing goal:', error);
      throw error;
    }
  },

  // ============================================================
  // LEAD MEASURES
  // ============================================================

  async addLeadMeasure(bookId, goalId, measureData) {
    try {
      const measureId = `lm_${Date.now()}`;
      const measure = {
        ...DEFAULT_LEAD_MEASURE,
        ...measureData,
        id: measureId
      };
      
      const docRef = doc(db, 'pb-books', bookId, 'goals', goalId);
      await updateDoc(docRef, {
        leadMeasures: arrayUnion(measure),
        updatedAt: serverTimestamp()
      });
      
      return measure;
    } catch (error) {
      console.error('Error adding lead measure:', error);
      throw error;
    }
  },

  async updateLeadMeasure(bookId, goalId, measureId, updates) {
    try {
      const goal = await this.getGoal(bookId, goalId);
      if (!goal) throw new Error('Goal not found');
      
      const updatedMeasures = goal.leadMeasures.map(m => 
        m.id === measureId ? { ...m, ...updates } : m
      );
      
      const docRef = doc(db, 'pb-books', bookId, 'goals', goalId);
      await updateDoc(docRef, {
        leadMeasures: updatedMeasures,
        updatedAt: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      console.error('Error updating lead measure:', error);
      throw error;
    }
  },

  async removeLeadMeasure(bookId, goalId, measureId) {
    try {
      const goal = await this.getGoal(bookId, goalId);
      if (!goal) throw new Error('Goal not found');
      
      const updatedMeasures = goal.leadMeasures.filter(m => m.id !== measureId);
      
      const docRef = doc(db, 'pb-books', bookId, 'goals', goalId);
      await updateDoc(docRef, {
        leadMeasures: updatedMeasures,
        updatedAt: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      console.error('Error removing lead measure:', error);
      throw error;
    }
  },

  async recordLeadMeasureProgress(bookId, goalId, measureId, weekNumber, actualValue) {
    try {
      const goal = await this.getGoal(bookId, goalId);
      if (!goal) throw new Error('Goal not found');
      
      const updatedMeasures = goal.leadMeasures.map(m => {
        if (m.id === measureId) {
          return {
            ...m,
            weeklyActuals: {
              ...m.weeklyActuals,
              [`week${weekNumber}`]: actualValue
            }
          };
        }
        return m;
      });
      
      const docRef = doc(db, 'pb-books', bookId, 'goals', goalId);
      await updateDoc(docRef, {
        leadMeasures: updatedMeasures,
        updatedAt: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      console.error('Error recording lead measure progress:', error);
      throw error;
    }
  },

  // ============================================================
  // WEEKLY SCORING
  // ============================================================

  async getWeeklyScores(bookId, goalId) {
    try {
      const scoresRef = collection(db, 'pb-books', bookId, 'weekly-scores');
      const q = query(
        scoresRef, 
        where('goalId', '==', goalId),
        orderBy('weekNumber', 'asc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting weekly scores:', error);
      return [];
    }
  },

  async getWeeklyScore(bookId, goalId, weekNumber) {
    try {
      const scoresRef = collection(db, 'pb-books', bookId, 'weekly-scores');
      const q = query(
        scoresRef,
        where('goalId', '==', goalId),
        where('weekNumber', '==', weekNumber)
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs[0] ? { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } : null;
    } catch (error) {
      console.error('Error getting weekly score:', error);
      return null;
    }
  },

  async saveWeeklyScore(bookId, goalId, weekNumber, scoreData) {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('User must be authenticated');

      const goal = await this.getGoal(bookId, goalId);
      if (!goal) throw new Error('Goal not found');

      // Calculate lead measure scores
      const leadMeasureScores = goal.leadMeasures.map(measure => ({
        measureId: measure.id,
        description: measure.description,
        target: measure.targetPerWeek,
        actual: scoreData.leadMeasureActuals?.[measure.id] || measure.weeklyActuals?.[`week${weekNumber}`] || 0,
        score: 0 // Will be calculated
      }));

      // Calculate individual scores
      leadMeasureScores.forEach(score => {
        score.score = score.target > 0 
          ? Math.min(Math.round((score.actual / score.target) * 100), 100)
          : 0;
      });

      const overallScore = calculateWeeklyScore(leadMeasureScores);

      // Check if score exists
      const existingScore = await this.getWeeklyScore(bookId, goalId, weekNumber);
      
      const scoreDoc = {
        ...DEFAULT_WEEKLY_SCORE,
        ...scoreData,
        goalId,
        weekNumber,
        leadMeasureScores,
        overallScore,
        reviewedAt: serverTimestamp(),
        reviewedBy: currentUser.uid,
        updatedAt: serverTimestamp()
      };

      if (existingScore) {
        const docRef = doc(db, 'pb-books', bookId, 'weekly-scores', existingScore.id);
        await updateDoc(docRef, scoreDoc);
        return { id: existingScore.id, ...scoreDoc };
      } else {
        const scoresRef = collection(db, 'pb-books', bookId, 'weekly-scores');
        const docRef = doc(scoresRef);
        
        const newScoreDoc = {
            ...scoreDoc,
            id: docRef.id,  // ← Add ID
            createdAt: serverTimestamp()
        };
        
        await setDoc(docRef, newScoreDoc);  // ← Use setDoc
        
        // Update goal's weekly scores array
        await this.updateGoal(bookId, goalId, {
            weeklyScores: arrayUnion({
            weekNumber,
            score: overallScore,
            scoredAt: new Date().toISOString()
            })
        });
        
        return { id: docRef.id, ...newScoreDoc };
        }
            } catch (error) {
      console.error('Error saving weekly score:', error);
      throw error;
    }
  },

  // ============================================================
  // LAG MEASURE (RESULT) TRACKING
  // ============================================================

  async updateLagMeasure(bookId, goalId, currentValue) {
    try {
      const goal = await this.getGoal(bookId, goalId);
      if (!goal) throw new Error('Goal not found');
      
      const docRef = doc(db, 'pb-books', bookId, 'goals', goalId);
      await updateDoc(docRef, {
        'lagMeasure.currentValue': currentValue,
        overallProgress: calculateGoalProgress({ 
          ...goal, 
          lagMeasure: { ...goal.lagMeasure, currentValue } 
        }),
        updatedAt: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      console.error('Error updating lag measure:', error);
      throw error;
    }
  },

  // ============================================================
  // ANALYTICS & INSIGHTS
  // ============================================================

  async getGoalAnalytics(bookId, goalId) {
    try {
      const goal = await this.getGoal(bookId, goalId);
      if (!goal) return null;

      const weeklyScores = await this.getWeeklyScores(bookId, goalId);
      const currentWeek = goal.periodStartDate 
        ? getCurrentWeekNumber(goal.periodStartDate) 
        : 1;

      // Calculate averages and trends
      const scoredWeeks = weeklyScores.filter(s => s.overallScore > 0);
      const averageScore = scoredWeeks.length > 0
        ? Math.round(scoredWeeks.reduce((sum, s) => sum + s.overallScore, 0) / scoredWeeks.length)
        : 0;

      // Score trend (last 4 weeks)
      const recentScores = scoredWeeks.slice(-4);
      let trend = 'stable';
      if (recentScores.length >= 2) {
        const firstHalf = recentScores.slice(0, Math.floor(recentScores.length / 2));
        const secondHalf = recentScores.slice(Math.floor(recentScores.length / 2));
        const firstAvg = firstHalf.reduce((s, w) => s + w.overallScore, 0) / firstHalf.length;
        const secondAvg = secondHalf.reduce((s, w) => s + w.overallScore, 0) / secondHalf.length;
        
        if (secondAvg > firstAvg + 5) trend = 'improving';
        else if (secondAvg < firstAvg - 5) trend = 'declining';
      }

      // Lead measure performance
      const leadMeasureStats = goal.leadMeasures.map(measure => {
        const weeklyData = Object.entries(measure.weeklyActuals || {});
        const totalActual = weeklyData.reduce((sum, [_, val]) => sum + val, 0);
        const weeksTracked = weeklyData.length;
        const avgPerWeek = weeksTracked > 0 ? Math.round(totalActual / weeksTracked) : 0;
        const targetPerPeriod = measure.targetPerWeek * 12;
        const onTrack = avgPerWeek >= measure.targetPerWeek * 0.85;

        return {
          id: measure.id,
          description: measure.description,
          targetPerWeek: measure.targetPerWeek,
          avgPerWeek,
          totalActual,
          targetPerPeriod,
          percentComplete: targetPerPeriod > 0 ? Math.round((totalActual / targetPerPeriod) * 100) : 0,
          onTrack
        };
      });

      return {
        goalId,
        currentWeek,
        weeksRemaining: Math.max(12 - currentWeek, 0),
        averageScore,
        trend,
        totalWeeksScored: scoredWeeks.length,
        consecutiveWeeksAbove85: this.getConsecutiveWeeksAbove(scoredWeeks, 85),
        goalProgress: goal.calculatedProgress,
        leadMeasureStats,
        weeklyScores: weeklyScores.map(s => ({
          week: s.weekNumber,
          score: s.overallScore
        })),
        isOnTrack: averageScore >= 85,
        projectedCompletion: this.calculateProjectedCompletion(goal, averageScore)
      };
    } catch (error) {
      console.error('Error getting goal analytics:', error);
      return null;
    }
  },

  getConsecutiveWeeksAbove(scores, threshold) {
    let consecutive = 0;
    for (let i = scores.length - 1; i >= 0; i--) {
      if (scores[i].overallScore >= threshold) consecutive++;
      else break;
    }
    return consecutive;
  },

  calculateProjectedCompletion(goal, averageScore) {
    if (!goal.lagMeasure?.targetValue) return 'unknown';
    
    const progress = goal.calculatedProgress || 0;
    const currentWeek = goal.periodStartDate 
      ? getCurrentWeekNumber(goal.periodStartDate) 
      : 1;
    const weeksRemaining = 12 - currentWeek;
    
    if (progress >= 100) return 'completed';
    if (weeksRemaining === 0) return 'period_ended';
    
    const progressPerWeek = currentWeek > 0 ? progress / currentWeek : 0;
    const projectedFinal = progress + (progressPerWeek * weeksRemaining);
    
    if (projectedFinal >= 100) return 'on_track';
    if (projectedFinal >= 80) return 'possible';
    return 'at_risk';
  },

  async getPeriodDashboard(bookId, periodId) {
    if (!periodId) return null;
    try {
      const period = await this.getPeriod(bookId, periodId);
      if (!period) return null;

      const goals = await this.getGoals(bookId, { periodId });
      const currentWeek = getCurrentWeekNumber(period.startDate);
      
      // Get analytics for all goals
      const goalAnalytics = await Promise.all(
        goals.map(g => this.getGoalAnalytics(bookId, g.id))
      );

      const totalGoals = goals.length;
      const goalsOnTrack = goalAnalytics.filter(a => a?.isOnTrack).length;
      const averagePeriodScore = goalAnalytics.length > 0
        ? Math.round(goalAnalytics.reduce((sum, a) => sum + (a?.averageScore || 0), 0) / goalAnalytics.length)
        : 0;

      return {
        period,
        currentWeek,
        weeksRemaining: Math.max(12 - currentWeek, 0),
        daysRemaining: Math.max(0, Math.ceil((new Date(period.endDate) - new Date()) / (24 * 60 * 60 * 1000))),
        totalGoals,
        goalsOnTrack,
        goalsAtRisk: totalGoals - goalsOnTrack,
        averagePeriodScore,
        goals: goals.map((goal, i) => ({
          ...goal,
          analytics: goalAnalytics[i]
        }))
      };
    } catch (error) {
      console.error('Error getting period dashboard:', error);
      return null;
    }
  }
};

export default goalService;